// =============================================================================
// TrackerPage.jsx  (v2 — com Filtros e Gantt)
//
// Alterações relativamente à v1:
//   1. Estado de filtros (filters) — filtragem puramente local, sem chamadas API
//   2. useMemo → filteredEntries — entries após aplicar todos os filtros activos
//   3. Renderiza TrackerFilters acima da tabela
//   4. Renderiza TrackerGantt abaixo da tabela
//
// Lógica de filtragem temporal:
//   From → StartDate >= From
//   To   → StartDate <= To
// =============================================================================

import React, { useState, useEffect, useReducer, useCallback, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box, Button, Typography, Alert, Snackbar,
  Stack, Chip, CircularProgress, Tooltip,
} from '@mui/material';
import AddIcon      from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';

import TrackerTable   from './TrackerTable';
import TrackerModal   from './TrackerModal';
import SiteListDialog from './SiteListDialog';
import TrackerFilters from './TrackerFilters';
import TrackerGantt   from './TrackerGantt';

import trackerReducer                                  from '../reducer/trackerReducer';
import { loadEntries, loadEntry, saveEntry, removeEntry } from '../reducer/trackerThunks';
import { openModal, closeModal, clearError }           from '../actions/trackerActions';
import { apiExportAll }                                from '../requests/trackerRequests';


// =============================================================================
// useThunkReducer — suporta thunks (funções) além de actions (objectos)
// =============================================================================
const useThunkReducer = (reducer, initialState) => {
  const [state, rawDispatch] = useReducer(reducer, initialState);

  const dispatch = useCallback(
    (action) => {
      if (typeof action === 'function') {
        action(dispatch);
      } else {
        rawDispatch(action);
      }
    },
    [rawDispatch]
  );

  return [state, dispatch];
};


// ── State inicial do reducer ──────────────────────────────────────────────────
const initialState = {
  entries:     [],
  activeEntry: null,
  loading:     false,
  saving:      false,
  loadingOne:  false,
  error:       null,
  modalOpen:   false,
};

// ── State inicial dos filtros ─────────────────────────────────────────────────
const emptyFilters = {
  dateFrom:     null,   // 'yyyy-MM-dd' ou null
  dateTo:       null,   // 'yyyy-MM-dd' ou null
  technologies: [],     // string[]
  teams:        [],     // string[]
  owners:       [],     // string[]
  features:     [],     // string[]
};

const initialSnackbar = { open: false, message: '', severity: 'success' };


// =============================================================================
// Helpers de filtragem
// =============================================================================

// Converte 'yyyy-MM-dd' → timestamp (início do dia UTC)
const toMs = (dateStr) => dateStr ? new Date(dateStr + 'T00:00:00Z').getTime() : null;

// Converte string de data da API (vários formatos) → timestamp UTC
const parseDateMs = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();

  const slash = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (slash) return new Date(`${slash[3]}-${slash[1]}-${slash[2]}T00:00:00Z`).getTime();

  const dash = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (dash) {
    const [, y, a, b] = dash;
    const iso = Number(a) > 12 ? `${y}-${b}-${a}` : `${y}-${a}-${b}`;
    return new Date(`${iso}T00:00:00Z`).getTime();
  }

  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d.getTime();
};

// Aplica todos os filtros activos a um array de entries
const applyFilters = (entries, filters) => {
  const filtFrom = toMs(filters.dateFrom);
  const filtTo   = toMs(filters.dateTo);

  return entries.filter((e) => {
    // ── Filtro de datas ───────────────────────────────────────────────────────
    if (filtFrom || filtTo) {
      const actStart = parseDateMs(e.startDate);

      if (!actStart) return false;

      // From → StartDate >= From
      if (filtFrom && actStart < filtFrom) return false;

      // To   → StartDate <= To
      if (filtTo && actStart > filtTo) return false;
    }

    // ── Filtro de tecnologia ──────────────────────────────────────────────────
    if (filters.technologies.length > 0) {
      if (!filters.technologies.includes(e.technology)) return false;
    }

    // ── Filtro de equipa ──────────────────────────────────────────────────────
    if (filters.teams.length > 0) {
      if (!filters.teams.includes(e.teamName)) return false;
    }

    // ── Filtro de owner ───────────────────────────────────────────────────────
    if (filters.owners.length > 0) {
      const entryOwners = e.ownerNames ?? [];
      const hasMatch = entryOwners.some((o) => filters.owners.includes(o));
      if (!hasMatch) return false;
    }

    // ── Filtro de feature ─────────────────────────────────────────────────────
    if (filters.features.length > 0) {
      if (!filters.features.includes(e.feature)) return false;
    }

    return true;
  });
};


// =============================================================================
// COMPONENTE
// =============================================================================
const TrackerPage = () => {
  const [state, dispatch] = useThunkReducer(trackerReducer, initialState);
  const { entries, activeEntry, loading, saving, loadingOne, error, modalOpen } = state;

  // userId do utilizador autenticado — enviado para a API para o audit log
  const userId = useSelector((s) => s.session.user.id);

  const [snackbar, setSnackbar] = useState(initialSnackbar);
  const [filters,  setFilters]  = useState(emptyFilters);

  // ── SiteListDialog — fora do TrackerModal para evitar bug de backdrops ────
  const [siteListOpen,     setSiteListOpen]     = useState(false);
  const [siteListServer,   setSiteListServer]   = useState(null);
  const [siteListCallback, setSiteListCallback] = useState(null);


  // ── Entries filtradas (memo — só recalcula quando entries ou filters mudam) ─
  const filteredEntries = useMemo(
    () => applyFilters(entries, filters),
    [entries, filters]
  );


  // ── Snackbar helpers ──────────────────────────────────────────────────────
  const showSnack  = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });
  const closeSnack = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));


  // ── Carregar tabela ao montar ─────────────────────────────────────────────
  useEffect(() => {
    dispatch(loadEntries());
  }, []);


  // ── Snackbar após operações de escrita ────────────────────────────────────
  const prevSaving     = useRef(false);
  const lastOpIsDelete = useRef(false);
  useEffect(() => {
    if (prevSaving.current && !saving && !error) {
      showSnack(
        lastOpIsDelete.current ? 'Done.' : 'Done.',
        lastOpIsDelete.current ? 'info' : 'success'
      );
      lastOpIsDelete.current = false;
    }
    prevSaving.current = saving;
  }, [saving, error]);  // eslint-disable-line react-hooks/exhaustive-deps


  // ── Handlers da tabela e modal ────────────────────────────────────────────

  const handleRowClick = (row) => {
    const normalizedRow = {
      ...row,
      teamId:  row.teamId  != null ? String(row.teamId)  : '',
      ownerId: row.ownerId != null ? String(row.ownerId) : '',
    };
    dispatch(openModal(normalizedRow));
    dispatch(loadEntry(row.id));
  };

  const handleAddNew     = ()         => dispatch(openModal(null));
  const handleCloseModal = ()         => dispatch(closeModal());
  const handleSave = (formData) => {
    lastOpIsDelete.current = false;
    dispatch(saveEntry(formData, userId));
  };

  const handleDeleteFromTable = (id, entryTitle) => {
    if (!window.confirm('Remove this activity? This cannot be undone.')) return;
    lastOpIsDelete.current = true;
    dispatch(removeEntry(id, userId, entryTitle));
  };

  const handleDeleteFromModal = (id, entryTitle) => {
    lastOpIsDelete.current = true;
    dispatch(removeEntry(id, userId, entryTitle));
  };


  // ── Handlers do SiteListDialog ────────────────────────────────────────────

  const handleOpenSiteList = (server, updateServerFn) => {
    setSiteListServer(server);
    setSiteListCallback(() => updateServerFn);
    setSiteListOpen(true);
  };

  const handleCloseSiteList = () => {
    setSiteListOpen(false);
    setTimeout(() => {
      setSiteListServer(null);
      setSiteListCallback(null);
    }, 300);
  };

  const handleConfirmSiteList = (selectedSites) => {
    if (siteListCallback) siteListCallback(selectedSites);
    handleCloseSiteList();
  };


  // ── Chips de contagem por tecnologia (sobre as entries filtradas) ─────────
  const techCounts = ['2G', '3G', '4G', '5G']
    .map((t) => ({ t, n: filteredEntries.filter((e) => e.technology === t).length }))
    .filter((x) => x.n > 0);

  const isFiltered = filteredEntries.length < entries.length;


  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 2, md: 4 } }}>

      {/* ── Cabeçalho ──────────────────────────────────────────────────────── */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} letterSpacing={-0.5}>
            Network Activity Tracker
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
            <Typography variant="caption" color="text.secondary">
              {isFiltered
                ? <><strong>{filteredEntries.length}</strong> of {entries.length} activities</>
                : <>{entries.length} {entries.length === 1 ? 'activity' : 'activities'}</>
              }
            </Typography>
            {techCounts.map(({ t, n }) => (
              <Chip key={t} label={`${t}: ${n}`} size="small"
                sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
            ))}
          </Stack>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {loadingOne && <CircularProgress size={20} />}
          <Tooltip title="Export all activities to Excel">
            <span>
              <Button
                variant="outlined" startIcon={<DownloadIcon />}
                onClick={() => apiExportAll()}
                disabled={entries.length === 0}
                disableElevation
              >
                Export All
              </Button>
            </span>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddNew} disableElevation>
            New Activity
          </Button>
        </Box>
      </Stack>

      {/* ── Banner de erro global ───────────────────────────────────────────── */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearError())}>
          {error}
        </Alert>
      )}

      {/* ── Barra de filtros ────────────────────────────────────────────────── */}
      <TrackerFilters
        entries={entries}
        filters={filters}
        onChange={setFilters}
        onClear={() => setFilters(emptyFilters)}
        filtered={filteredEntries.length}
      />

      {/* ── Tabela (recebe as entries JÁ filtradas) ─────────────────────────── */}
      <TrackerTable
        entries={filteredEntries}
        loading={loading || loadingOne}
        onRowClick={handleRowClick}
        onDelete={handleDeleteFromTable}
      />

      {/* ── Gantt (abaixo da tabela, mesmas entries filtradas) ──────────────── */}
      <TrackerGantt entries={filteredEntries} onRowClick={handleRowClick} />

      {/* ── Modal de criar/editar ───────────────────────────────────────────── */}
      <TrackerModal
        open={modalOpen}
        entry={activeEntry}
        saving={saving}
        onSave={handleSave}
        onClose={handleCloseModal}
        onDelete={handleDeleteFromModal}
        onOpenSiteList={handleOpenSiteList}
      />

      {/*
        SiteListDialog renderizado aqui, ao lado do TrackerModal — nunca dentro.
        Assim os dois Dialogs são independentes no DOM e o fecho de um
        nunca afecta o outro.
      */}
      <SiteListDialog
        open={siteListOpen}
        server={siteListServer}
        onClose={handleCloseSiteList}
        onConfirm={handleConfirmSiteList}
      />

      {/* ── Snackbar de feedback ─────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default TrackerPage;